import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube, Linkedin } from "lucide-react";
import { SITE_LOGO_SRC, CONTACT_EMAIL, LINKEDIN_URL } from "@/lib/site";
import { BROWSE_CATEGORY_CARDS } from "@/data/categoryListings";

const SOCIAL_LINKS = [
  { id: "instagram", href: "https://www.instagram.com/homerajrealty/", label: "Instagram", Icon: Instagram },
  { id: "facebook", href: "https://www.facebook.com/watch/phdrealestatewala/", label: "Facebook", Icon: Facebook },
  { id: "youtube", href: "https://youtube.com/@homerajrealtygurgaon?si=L5-xKwX-cJi2MaK1", label: "YouTube", Icon: Youtube },
  { id: "linkedin", href: LINKEDIN_URL, label: "LinkedIn", Icon: Linkedin },
];

const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "Projects", to: "/projects" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div>
            <Link to="/" className="inline-block">
              <img
                src={SITE_LOGO_SRC}
                alt="Homeraj Realty"
                width={220}
                height={52}
                className="h-10 sm:h-11 w-auto object-contain object-left"
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p className="text-white/50 text-sm font-light mt-4 leading-relaxed">
              Premium property consultant in Gurugram specialising in luxury residential, commercial, and investment properties.
            </p>
            <div className="flex gap-4 mt-6">
              {SOCIAL_LINKS.map(({ id, href, label, Icon }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${id}`}
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/50 hover:text-[#C5A880] hover:border-[#C5A880] transition-colors"
                >
                  <Icon className="w-4 h-4" aria-hidden />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-medium text-sm uppercase tracking-widest mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {QUICK_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                    className="text-white/50 text-sm font-light hover:text-[#C5A880] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-medium text-sm uppercase tracking-widest mb-6">Property categories</h4>
            <ul className="space-y-3">
              {BROWSE_CATEGORY_CARDS.map((cat) => (
                <li key={cat.slug}>
                  <Link
                    to={`/categories/${cat.slug}`}
                    data-testid={`footer-category-${cat.slug}`}
                    className="text-white/50 text-sm font-light hover:text-[#C5A880] transition-colors"
                  >
                    {cat.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-medium text-sm uppercase tracking-widest mb-6">Contact</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#C5A880] mt-1 shrink-0" />
                <p className="text-white/50 text-sm font-light">
                  Office N 5, Near Godrej Meridian, Sector 106, Gurugram
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#C5A880] shrink-0" />
                <a
                  href="tel:9310965215"
                  className="text-white/50 text-sm font-light hover:text-[#C5A880] transition-colors"
                >
                  93109 65215
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#C5A880] shrink-0" />
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-white/50 text-sm font-light hover:text-[#C5A880] transition-colors break-all"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 text-center">
          <p className="text-white/30 text-sm font-light">&copy; 2023 Homeraj Realty. All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
}
